import { useState, useEffect } from 'react';
import { ArrowLeft, Save, RefreshCw, Settings2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { useDailyPickThresholds, useUpdateAppSetting, DailyPickThresholds } from '@/hooks/useAppSettings';
import { Skeleton } from '@/components/ui/skeleton';

const formatMarketCap = (value: number) => {
  if (value >= 1e9) return `A$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `A$${(value / 1e6).toFixed(0)}M`;
  return `A$${value.toLocaleString()}`;
};

const DailyPickSettings = () => {
  const { data: settings, isLoading, error, refetch } = useDailyPickThresholds();
  const updateSetting = useUpdateAppSetting<DailyPickThresholds>();

  const [formData, setFormData] = useState<DailyPickThresholds>({
    min_market_cap: 250_000_000,
    max_market_cap: 250_000_000_000,
    min_consideration_score: 45,
    min_pick_score: 50,
    hot_pick_score: 70,
    strong_contender_score: 60,
  });

  useEffect(() => {
    if (settings?.value) {
      setFormData(settings.value);
    }
  }, [settings]);

  const handleSave = async () => {
    try {
      await updateSetting.mutateAsync({
        id: 'daily_pick_thresholds',
        value: formData,
      });
      toast.success('Settings saved successfully');
    } catch (err) {
      toast.error('Failed to save settings');
      console.error(err);
    }
  };

  const handleReset = () => {
    setFormData({
      min_market_cap: 250_000_000,
      max_market_cap: 250_000_000_000,
      min_consideration_score: 45,
      min_pick_score: 50,
      hot_pick_score: 70,
      strong_contender_score: 60,
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-3xl mx-auto space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-3xl mx-auto">
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="text-destructive">Error Loading Settings</CardTitle>
              <CardDescription>{String(error)}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => refetch()}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Settings2 className="w-6 h-6" />
              Daily Pick Settings
            </h1>
            <p className="text-muted-foreground text-sm">
              Configure thresholds for the daily token selection algorithm
            </p>
          </div>
        </div>

        {/* Market Cap Range */}
        <Card>
          <CardHeader>
            <CardTitle>Market Cap Range</CardTitle>
            <CardDescription>
              Only tokens within this market cap range will be considered for the daily pick
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Minimum Market Cap</Label>
                <Input
                  type="number"
                  value={formData.min_market_cap}
                  onChange={(e) => setFormData({ ...formData, min_market_cap: Number(e.target.value) })}
                />
                <p className="text-xs text-muted-foreground">{formatMarketCap(formData.min_market_cap)}</p>
              </div>
              <div className="space-y-2">
                <Label>Maximum Market Cap</Label>
                <Input
                  type="number"
                  value={formData.max_market_cap}
                  onChange={(e) => setFormData({ ...formData, max_market_cap: Number(e.target.value) })}
                />
                <p className="text-xs text-muted-foreground">{formatMarketCap(formData.max_market_cap)}</p>
              </div>
            </div>

            {/* Quick presets */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFormData({ ...formData, min_market_cap: 10_000_000, max_market_cap: 500_000_000 })}
              >
                Small Caps (A$10M - A$500M)
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFormData({ ...formData, min_market_cap: 500_000_000, max_market_cap: 10_000_000_000 })}
              >
                Mid Caps (A$500M - A$10B)
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFormData({ ...formData, min_market_cap: 250_000_000, max_market_cap: 250_000_000_000 })}
              >
                Wide Range (A$250M - A$250B)
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Score Thresholds */}
        <Card>
          <CardHeader>
            <CardTitle>Score Thresholds</CardTitle>
            <CardDescription>
              Define the minimum scores required for different rating categories
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <div className="space-y-4">
              <div className="flex justify-between">
                <Label>Minimum Consideration Score</Label>
                <span className="font-mono text-sm">{formData.min_consideration_score}</span>
              </div>
              <Slider
                value={[formData.min_consideration_score]}
                onValueChange={([v]) => setFormData({ ...formData, min_consideration_score: v })}
                min={0}
                max={100}
                step={1}
              />
              <p className="text-xs text-muted-foreground">
                Tokens scoring below this won't appear as candidates at all
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between">
                <Label>Minimum Pick Score (WATCHLIST)</Label>
                <span className="font-mono text-sm">{formData.min_pick_score}</span>
              </div>
              <Slider
                value={[formData.min_pick_score]}
                onValueChange={([v]) => setFormData({ ...formData, min_pick_score: v })}
                min={0}
                max={100}
                step={1}
              />
              <p className="text-xs text-muted-foreground">
                Below this score = "No Strong Pick Today" message
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between">
                <Label>Strong Contender Score</Label>
                <span className="font-mono text-sm text-yellow-500">{formData.strong_contender_score}</span>
              </div>
              <Slider
                value={[formData.strong_contender_score]}
                onValueChange={([v]) => setFormData({ ...formData, strong_contender_score: v })}
                min={0}
                max={100}
                step={1}
              />
            </div>

            <div className="space-y-4">
              <div className="flex justify-between">
                <Label>Hot Pick Score</Label>
                <span className="font-mono text-sm text-green-500">{formData.hot_pick_score}</span>
              </div>
              <Slider
                value={[formData.hot_pick_score]}
                onValueChange={([v]) => setFormData({ ...formData, hot_pick_score: v })}
                min={0}
                max={100}
                step={1}
              />
              <p className="text-xs text-muted-foreground">
                Tokens at or above this score get the "HOT PICK" badge
              </p>
            </div>

            {/* Visual preview */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium">Rating Preview:</p>
              <div className="flex flex-wrap gap-2 text-sm">
                <span className="px-2 py-1 rounded bg-muted">0-{formData.min_consideration_score - 1}: Hidden</span>
                <span className="px-2 py-1 rounded bg-muted">{formData.min_consideration_score}-{formData.min_pick_score - 1}: No Pick</span>
                <span className="px-2 py-1 rounded bg-blue-500/20 text-blue-400">{formData.min_pick_score}-{formData.strong_contender_score - 1}: WATCHLIST</span>
                <span className="px-2 py-1 rounded bg-yellow-500/20 text-yellow-400">{formData.strong_contender_score}-{formData.hot_pick_score - 1}: STRONG CONTENDER</span>
                <span className="px-2 py-1 rounded bg-green-500/20 text-green-400">{formData.hot_pick_score}+: HOT PICK</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={handleReset}>
            Reset to Defaults
          </Button>
          <Button onClick={handleSave} disabled={updateSetting.isPending}>
            {updateSetting.isPending ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>

        {/* Last updated info */}
        {settings?.updated_at && (
          <p className="text-xs text-muted-foreground text-center">
            Last updated: {new Date(settings.updated_at).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
};

export default DailyPickSettings;
